// src/components/album/FlipCardContent.jsx
import React, { useState, useRef } from 'react';
import { 
  ArrowLeft,
  Send,
  Users,
  Star,
  Plus,
  MessageCircle,
  Heart,
  ShoppingCart,
  Calendar,
  User,
  FileText,
  MessageSquare,
  HelpCircle,
  Check,
  X,
  ExternalLink,
  Download,
  Share2,
  Loader2,
  AlertCircle,
  Clock,
  MapPin,
  DollarSign,
  Package,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';
import { flipCardService } from '../../services/flipCardService';
import { FlipCardForm } from './content/FlipCardForm';
import { ContentCreationModal } from './modals/ContentCreationModal';
import { QuickStatusForm } from '../team/QuickStatusForm';

export const FlipCardContent = ({
  content,
  mediaItem,
  bookId,
  galleryId,
  isOwner,
  primaryColor,
  onFlipBack,
  onUpdate,
  onAddContent,
  isLoading,
  bookData,
  showBackButton = true
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizScore, setQuizScore] = useState(0)
  const [showContentForm, setShowContentForm] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false)
  const [contentType, setContentType] = useState('');
  
  const handleAddContent = () => {
    if (onUpdate) {
      onUpdate('openContentModal', { mediaItem, galleryId });
    }
  };

  const handleContentTypeSelect = (type) => {
    setContentType(type);
    // Keep the form open to show the selected content type form
  };
  // Form handling functions
  const handleInputChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: null }));
    }
  };

  const handleFormClose = () => {
    setShowContentForm(false);
    setContentType('');
  };

  const handleModalClose = () => {
    setShowContentModal(false);
  };

  const validateForm = () => {
    const newErrors = {};
    
    content.fields?.forEach(field => {
      if (field.required && !formData[field.id]) {
        newErrors[field.id] = `${field.label} is required`;
      }
      
      if (field.type === 'email' && formData[field.id] && !isValidEmail(formData[field.id])) {
        newErrors[field.id] = 'Please enter a valid email address';
      }
    });
    
    return newErrors;
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleContentCreated = (newContent) => {
    // Update the media item with the new back content
    setShowContentForm(false);
    setContentType('');
    // Trigger parent update
    if (onUpdate) {
      onUpdate();
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      await flipCardService.submitFormResponse(bookId, galleryId, content.id, formData);
      setSubmitStatus('success');
      setFormData({});
      
      setTimeout(() => setSubmitStatus(null), 3000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Quiz handling functions
  const handleQuizAnswer = (questionId, answer) => {
    setQuizAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleQuizSubmit = async () => {
    const questions = content.questions || [];
    let score = 0;
    
    questions.forEach(question => {
      if (quizAnswers[question.id] === question.correctAnswer) {
        score++;
      }
    });
    
    setQuizScore(score);
    setQuizCompleted(true);
    
    // Track quiz completion
    await flipCardService.trackContentInteraction(bookId, galleryId, content.id, 'quiz_complete', {
      score,
      totalQuestions: questions.length,
      percentage: Math.round((score / questions.length) * 100)
    });
  };

  const handleAction = async (action) => {
    switch (action.type) {
      case 'url':
        if (action.url) {
          window.open(action.url, action.target || '_blank');
          // Track external link click
          await flipCardService.trackContentInteraction(bookId, galleryId, content.id, 'external_link', {
            url: action.url,
            label: action.label
          });
        }
        break;
      case 'cart':
        // Handle add to cart
        console.log('Add to cart:', content);
        break;
      case 'booking':
        // Handle booking
        console.log('Book event:', content);
        break;
      default:
        console.log('Action triggered:', action);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-full bg-white rounded-lg shadow-lg flex items-center justify-center relative">
        {showBackButton && (
          <button
            onClick={(e) => {
              e.stopPropagation(); // 👈 Prevents event from bubbling up
              onFlipBack();        // 👈 Executes your existing flip-back logic
            }}
            className="absolute top-3 left-3 z-10 p-2 bg-white bg-opacity-90 text-gray-600 rounded-full hover:bg-opacity-100 transition-all shadow-sm"
            title="Flip back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Loading content...</p>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="w-full h-full bg-white rounded-lg shadow-lg flex flex-col items-center justify-center p-4">
        {showBackButton && (
          <button
            onClick={(e) => {
              e.stopPropagation(); // 👈 Prevents event from bubbling up
              onFlipBack();        // 👈 Executes your existing flip-back logic
            }}
            className="absolute top-3 left-3 z-10 p-2 bg-white bg-opacity-90 text-gray-600 rounded-full hover:bg-opacity-100 transition-all shadow-sm"
            title="Flip back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <div className="text-center">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">No Content Available</h3>
          <p className="text-sm text-gray-500 mb-4">This card doesn't have back-side content yet.</p>
          {isOwner && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onAddContent(mediaItem, galleryId);
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors flex items-center gap-2 mx-auto"
              style={{ backgroundColor: primaryColor || '#3b82f6' }}
            >
              <Plus className="w-4 h-4" />
              Add Content
            </button>
          )}
        </div>
      </div>
    );
  }

       
  // Render different content types
  const renderContent = () => {
    switch (content.type) {
      case 'form':
      case 'survey':
        return renderForm();
      case 'article':
      case 'blog':
        return renderArticle();
      case 'quiz':
      case 'question':
        return renderQuiz();
      case 'product':
      case 'item':
        return renderProduct();
      case 'event':
      case 'booking':
        return renderEvent();
      case 'profile':
      case 'bio':
        return renderProfile();
      case 'team_member':
      case 'quick_status':
        return <QuickStatusForm
          bookId={bookId}
          galleryId={galleryId}
          mediaItem={mediaItem}
          statusConfig={content.statusConfig}
          bookData={bookData}
          onSuccess={() => {
            if (onUpdate) onUpdate();
            if (onFlipBack) onFlipBack();
          }}
          onCancel={onFlipBack}
        />;
      default:
        return renderGeneric();
    }
  };


  const renderForm = () => {
    if (submitStatus === 'success') {
      return (
        <div className="w-full h-full bg-white rounded-lg shadow-lg flex items-center justify-center p-4">
          <div className="text-center">
            <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">Thank You!</h3>
            <p className="text-sm text-gray-600">
              {content.successMessage || 'Your response has been submitted successfully.'}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="w-full h-full bg-white rounded-lg shadow-lg overflow-hidden flip-content-form">
        <div className="p-4 h-full flex flex-col">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              {content.title || 'Form'}
            </h3>
            {content.description && (
              <p className="text-sm text-gray-600">{content.description}</p>
            )}
          </div>

          <form onSubmit={handleFormSubmit} className="flex-1 flex flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto">
              {content.fields?.map(field => (
                <div key={field.id}>
                  <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderFormField(field)}
                  {errors[field.id] && (
                    <p className="text-red-500 text-xs mt-1">{errors[field.id]}</p>
                  )}
                </div>
              ))}
            </div>

            {submitStatus === 'error' && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                <AlertCircle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-red-700">Failed to submit. Please try again.</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{ backgroundColor: primaryColor }}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {isSubmitting ? 'Submitting...' : (content.submitText || 'Submit')}
            </button>
          </form>
        </div>
      </div>
    );
  };

  const renderFormField = (field) => {
    const commonProps = {
      id: field.id,
      value: formData[field.id] || '',
      onChange: (e) => handleInputChange(field.id, e.target.value),
      className: `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
        errors[field.id] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
      }`,
      placeholder: field.placeholder
    };

    switch (field.type) {
      case 'textarea':
        return <textarea {...commonProps} rows={3} />;
      case 'select':
        return (
          <select {...commonProps}>
            <option value="">Select an option</option>
            {field.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      case 'rating':
        return (
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].slice(0, field.maxRating || 5).map(rating => (
              <button
                key={rating}
                type="button"
                onClick={() => handleInputChange(field.id, rating)}
                className={`p-1 ${
                  formData[field.id] >= rating ? 'text-yellow-400' : 'text-gray-300'
                }`}
              >
                <Star className="w-6 h-6 fill-current" />
              </button>
            ))}
          </div>
        );
      default:
        return <input {...commonProps} type={field.type || 'text'} />;
    }
  };

  const renderArticle = () => (
    <div className="w-full h-full bg-white rounded-lg shadow-lg overflow-hidden flip-content-article">
      <div className="p-4 h-full flex flex-col">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-1">
            {content.title}
          </h3>
          {content.author && (
            <p className="text-xs text-gray-500 mb-2">By {content.author}</p>
          )}
          {content.description && (
            <p className="text-sm text-gray-600">{content.description}</p>
          )}
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {content.content ? (
            <div 
              className="prose prose-sm max-w-none text-sm"
              dangerouslySetInnerHTML={{ __html: content.content }}
            />
          ) : (
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {content.text || 'Article content...'}
            </p>
          )}
        </div>
        
        {content.readTime && (
          <div className="flex items-center gap-1 text-xs text-gray-500 mt-3 pt-3 border-t">
            <Clock className="w-3 h-3" />
            {content.readTime} min read
          </div>
        )}
      </div>
    </div>
  );

  const renderQuiz = () => {
    if (quizCompleted) {
      return (
        <div className="w-full h-full bg-white rounded-lg shadow-lg flex items-center justify-center p-4">
          <div className="text-center">
            <div className="mb-4">
              {quizScore === content.questions?.length ? (
                <Check className="w-12 h-12 text-green-500 mx-auto" />
              ) : (
                <Star className="w-12 h-12 text-yellow-500 mx-auto" />
              )}
            </div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">Quiz Complete!</h3>
            <p className="text-sm text-gray-600 mb-4">
              You scored {quizScore} out of {content.questions?.length || 0}
            </p>
            <div className="text-2xl font-bold text-blue-600 mb-4">
              {Math.round((quizScore / (content.questions?.length || 1)) * 100)}%
            </div>
            {content.allowRetry && (
              <button
                onClick={() => {
                  setQuizCompleted(false);
                  setQuizAnswers({});
                  setQuizScore(0);
                  setCurrentQuestionIndex(0);
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      );
    }

    const currentQuestion = content.questions?.[currentQuestionIndex];
    if (!currentQuestion) return null;

    return (
      <div className="w-full h-full bg-white rounded-lg shadow-lg overflow-hidden flip-content-quiz">
        <div className="p-4 h-full flex flex-col">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-800">
                {content.title || 'Quiz'}
              </h3>
              <span className="text-xs text-gray-500">
                {currentQuestionIndex + 1} of {content.questions?.length || 0}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${((currentQuestionIndex + 1) / (content.questions?.length || 1)) * 100}%` 
                }}
              />
            </div>
          </div>

          <div className="flex-1 flex flex-col">
            <div className="mb-4">
              <h4 className="text-base font-medium text-gray-800 mb-3">
                {currentQuestion.question}
              </h4>
              
              <div className="space-y-2">
                {currentQuestion.options?.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuizAnswer(currentQuestion.id, index)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      quizAnswers[currentQuestion.id] === index
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
                disabled={currentQuestionIndex === 0}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {currentQuestionIndex === (content.questions?.length || 1) - 1 ? (
                <button
                  onClick={handleQuizSubmit}
                  disabled={!quizAnswers[currentQuestion.id]}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Submit Quiz
                </button>
              ) : (
                <button
                  onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                  disabled={!quizAnswers[currentQuestion.id]}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderProduct = () => (
    <div className="w-full h-full bg-white rounded-lg shadow-lg overflow-hidden flip-content-product">
      <div className="p-4 h-full flex flex-col">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-1">
            {content.title}
          </h3>
          {content.price > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl font-bold text-green-600">
                {content.currency || 'ZAR'} {content.price}
              </span>
              {!content.inStock && (
                <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                  Out of Stock
                </span>
              )}
            </div>
          )}
          {content.description && (
            <p className="text-sm text-gray-600">{content.description}</p>
          )}
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {content.features && content.features.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-800 mb-2">Features:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                {content.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        {content.actions && content.actions.length > 0 && (
          <div className="border-t pt-3 mt-3">
            <div className="flex flex-wrap gap-2">
              {content.actions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => handleAction(action)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
                  style={{ backgroundColor: primaryColor }}
                >
                  {getActionIcon(action.icon)}
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderEvent = () => (
    <div className="w-full h-full bg-white rounded-lg shadow-lg overflow-hidden flip-content-event">
      <div className="p-4 h-full flex flex-col">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {content.title}
          </h3>
          
          <div className="space-y-2 text-sm text-gray-600">
            {content.date && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(content.date).toLocaleDateString('en-ZA', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            )}
            
            {content.location && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {content.location}
              </div>
            )}
            
            {content.price > 0 && (
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                {content.currency || 'ZAR'} {content.price}
              </div>
            )}
            
            {content.availableSpots && (
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {content.availableSpots} spots available
              </div>
            )}
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {content.description && (
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {content.description}
            </p>
          )}
        </div>
        
        {content.actions && content.actions.length > 0 && (
          <div className="border-t pt-3 mt-3">
            <div className="flex flex-wrap gap-2">
              {content.actions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => handleAction(action)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
                  style={{ backgroundColor: primaryColor }}
                >
                  {getActionIcon(action.icon)}
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderProfile = () => (
    <div className="w-full h-full bg-white rounded-lg shadow-lg overflow-hidden flip-content-profile">
      <div className="p-4 h-full flex flex-col">
        <div className="text-center mb-4">
          {content.avatar && (
            <img 
              src={content.avatar} 
              alt={content.title}
              className="w-16 h-16 rounded-full mx-auto mb-2 object-cover"
            />
          )}
          <h3 className="text-lg font-semibold text-gray-800">
            {content.title}
          </h3>
          {content.subtitle && (
            <p className="text-sm text-gray-600">{content.subtitle}</p>
          )}
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {content.description && (
            <p className="text-sm text-gray-700 mb-4 whitespace-pre-wrap">
              {content.description}
            </p>
          )}
          
          {content.skills && content.skills.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-800 mb-2">Skills:</h4>
              <div className="flex flex-wrap gap-1">
                {content.skills.map((skill, index) => (
                  <span 
                    key={index}
                    className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {content.contact && (
          <div className="border-t pt-3 mt-3">
            <button
              onClick={() => handleAction({ type: 'url', url: `mailto:${content.contact}` })}
              className="w-full flex items-center justify-center gap-2 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
              style={{ backgroundColor: primaryColor }}
            >
              <MessageSquare className="w-4 h-4" />
              Contact
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderGeneric = () => (
    <div className="w-full h-full bg-white rounded-lg shadow-lg">
      <div className="p-4 h-full flex flex-col">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {content.title || 'Content'}
          </h3>
          {content.description && (
            <p className="text-sm text-gray-600 mb-4">
              {content.description}
            </p>
          )}
          
          {content.html && (
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: content.html }}
            />
          )}
          
          {content.text && (
            <div className="whitespace-pre-wrap text-sm text-gray-700">
              {content.text}
            </div>
          )}
        </div>
        
        {content.actions && content.actions.length > 0 && (
          <div className="border-t pt-3 mt-3">
            <div className="flex flex-wrap gap-2">
              {content.actions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => handleAction(action)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
                >
                  {getActionIcon(action.icon)}
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const getActionIcon = (iconName) => {
    const iconMap = {
      send: Send,
      star: Star,
      heart: Heart,
      cart: ShoppingCart,
      calendar: Calendar,
      user: User,
      file: FileText,
      message: MessageSquare,
      question: HelpCircle,
      check: Check,
      x: X,
      external: ExternalLink,
      download: Download,
      share: Share2
    };
    
    const IconComponent = iconMap[iconName] || Send;
    return <IconComponent className="w-4 h-4" />;
  };

  return (
    <div className="w-full h-full relative">
      {/* Back button */}
      <button
        onClick={(e) => {
          e.stopPropagation(); // 👈 Prevents event from bubbling up
          onFlipBack();        // 👈 Executes your existing flip-back logic
        }}
          className="absolute top-3 left-3 z-10 p-2 bg-white bg-opacity-90 text-gray-600 rounded-full hover:bg-opacity-100 transition-all shadow-sm"
           title="Flip back"
        >
        <ArrowLeft className="w-4 h-4" />
      </button>

      {/* Content */}
      {renderContent()}
    </div>
  );
};